import {
  BASIC_TEXT_BRANDING_QUERY,
  KARAOKE_TEXT_BRANDING_QUERY,
  SHOWCASE_BRANDING_QUERY,
} from './branding-gql';
import { COMPOSITION_TEMPLATE_QUERY_FRAGMENT } from './compositions-gql';
import {
  MEDIA_QUERY_FRAGMENT,
  SONG_QUERY_FRAGMENT,
  VIDEO_FILE_GQL_FRAGMENT,
} from './media-gql';
import {
  CUSTOMER_TYPE_QUERY_FRAGMENT,
  TRANSCRIPTION_QUERY_FRAGMENT,
} from './misc-gql';
import {
  REFERENCING_CAPTION,
  REFERENCING_SHARED_CONTENT,
} from './shared-content-gql';

import type { RequestDocument } from 'graphql-request';

export const STORY_QUERY: RequestDocument = `query getStory($id: ItemId) {
  story(filter: {id: {eq: $id}}) {
    id
    slug
    hash
    title(locale: en)
    description(locale: en)
    brollPage
    contributors {
      id
      name
      email
    }
    primaryShowcase {
      id
      title
      organization
      slug
      logo {
        url
      }
      mainImage {
        url
      }
      organizationArtifacts ${MEDIA_QUERY_FRAGMENT}
      organizationArtifactsVideo ${MEDIA_QUERY_FRAGMENT}
      organizationLogos ${MEDIA_QUERY_FRAGMENT}
      organizationAudios ${SONG_QUERY_FRAGMENT}
    }
    _allReferencingShowcases {
      id
      title
      organization
      slug
      logo {
        url
      }
      mainImage {
        url
      }
      profiles {
        id
        name
        platforms
      }
      customerType ${CUSTOMER_TYPE_QUERY_FRAGMENT}
      organizationLogos ${MEDIA_QUERY_FRAGMENT}
      organizationArtifacts ${MEDIA_QUERY_FRAGMENT}
      organizationArtifactsVideo ${MEDIA_QUERY_FRAGMENT}
      organizationAudios ${SONG_QUERY_FRAGMENT}
      lowerThirdTextBranding ${BASIC_TEXT_BRANDING_QUERY}
      karaokeTextBranding ${KARAOKE_TEXT_BRANDING_QUERY}
      compositionsBranding ${COMPOSITION_TEMPLATE_QUERY_FRAGMENT}
      quoteCardsBranding ${COMPOSITION_TEMPLATE_QUERY_FRAGMENT}
      lastUsedTemplates
      branding ${SHOWCASE_BRANDING_QUERY}
      allowLangSelectAtUserStoryUpload
      defaultLanguage
      defaultVideoTemplate { id }
      defaultPullQuoteTemplate { id }
      paymentFailed
    }
    thumbnail {
      url
    }
    aiGeneratedContent{
      id
      prompt
      generatedContent
    }
    _allReferencingSharedContents
    ${REFERENCING_SHARED_CONTENT}

    _allReferencingCaptions ${REFERENCING_CAPTION}

    alreadyGeneratedContent
    aiResponse
    byExternalUser
    externalUploadStatus
    savedBlog
    savedSocialPost
    savedTalkingPointContent
    savedEmail
    savedSegments
    shareableImages {
      id
      imagefile {
        id
        title
        url
        width
        height
        responsiveImage {
          srcSet
          alt
          title
        }
        _createdAt
      }
      quote
      storytellerName
      source
      metadata
      parentComposition {
        id
      }
      speaker {
        id
        name
      }
      _allReferencingSharedContents
      ${REFERENCING_SHARED_CONTENT}

      _allReferencingCaptions ${REFERENCING_CAPTION}
      _updatedAt
    }
    transcription ${TRANSCRIPTION_QUERY_FRAGMENT}
    originalVideo ${VIDEO_FILE_GQL_FRAGMENT}
    finalVideo {
      id
      title
      thumbnail {
        url
      }
      videoFilePrimary
        ${VIDEO_FILE_GQL_FRAGMENT}
      videoStatus
      sourcePlatform
    }
    otherVideos {
      id
      title
      videoStatus
      sourcePlatform
      isClientReady
      isDownloadEnabled
      isHidden
      clipJson
      reviewStatus
      assignee
      lastActionJson
      lastPublishJson
      thumbnail {
        url
      }
      shareableImageId
      videoFilePrimary
        ${VIDEO_FILE_GQL_FRAGMENT}
      slug
      hash
      aspectRatio
      videoJson
      resourcesMetadataJson
      transcriptionText
      aiGeneratedContent {
        id
        prompt
        generatedContent
      }
      associatedVideos {
        id
        slug
        hash
        aspectRatio
        videoStatus
        sourcePlatform
        isClientReady
        isHidden
        clipJson
        transcriptionText
        lastActionJson
        lastPublishJson
        videoJson
        thumbnail {
          url
        }
        shareableImageId
        videoFilePrimary
          ${VIDEO_FILE_GQL_FRAGMENT}
      }
      _allReferencingVideos {
        id
        videoStatus
        associatedVideos {
          id
          videoStatus
        }
      }
      _allReferencingSharedContents ${REFERENCING_SHARED_CONTENT}
      _allReferencingCaptions ${REFERENCING_CAPTION}
    }
    tags {
      name
    }
    storyTeller {
      name
    }
    storyArtifacts ${MEDIA_QUERY_FRAGMENT}
    storyAssets ${MEDIA_QUERY_FRAGMENT}
    aiPhotos ${MEDIA_QUERY_FRAGMENT}
    storyArtifactsVideo ${MEDIA_QUERY_FRAGMENT}
    myAudios ${SONG_QUERY_FRAGMENT}
    useAws
    uploadLog {
      id
      initialUpload
      submitToAuphonic
      ffmpegProcessing
      compressVideo
      generateWaveform
      combineResults
      extractThumbnailsFromLowRes
      handleFailure
    }
    clipThemesJson
  }
}`;

export const STORY_SPEAKERS_QUERY: RequestDocument = `query getStorySpeakers($id: ItemId) {
  story(filter: {id: {eq: $id}}) {
    speakers {
      id
      name
      firstName
      lastName
      title
      role
      organization
      voice
      bio
      headshots {
        id
        url
      }
    }
  }
}`;

// PLAT-11226: kept separate from NEW_STORIES_COMPLETION_QUERY on purpose —
// audioCorrected is a new story_steps_log field, and only the corrected-audio
// hot-swap subscription may depend on it. If the field is missing in a Dato
// environment, only that subscription errors (handled as a no-op); the
// dashboard completion query keeps working.
export const STORY_AUDIO_CORRECTED_QUERY: RequestDocument = `query getStoryAudioCorrected($id: ItemId) {
  story(filter: {id: {eq: $id}}) {
    id
    uploadLog {
      id
      audioCorrected
    }
  }
}`;

export const NEW_STORIES_COMPLETION_QUERY: RequestDocument = `query getStories($ids: [ItemId]) {
  allStories(filter: {id: {in: $ids}}) {
      id,
      externalUploadStatus
      aiResponse
      uploadLog {
        id
        initialUpload
        submitToAuphonic
        checkAuphonicProductionStatus
        transferFilesToProductionBucket
        ffmpegProcessing
        compressVideo
        generateWaveform
        awsTranscribe
        checkAwsTranscribeStatus
        revaiTranscribe
        checkRevaiTranscribeStatus
        combineResults
        extractThumbnailsFromLowRes
        handleFailure
      }
      useAws
      datoHostedVideo: originalVideo ${VIDEO_FILE_GQL_FRAGMENT}
      transcription ${TRANSCRIPTION_QUERY_FRAGMENT}
  }
}`;

export const STORY_REFERENCING_ALBUMS_ACCESS_QUERY: RequestDocument = `query getStory($id: ItemId) {
  story(filter: {id: {eq: $id}}) {
    albums: _allReferencingShowcases {
      id
      title
      organization
      slug
      adminList {
        ... on PersonRecord {
          email
        }
      }
      allowList {
        ... on PersonRecord {
          id
          email
          name
        }
      }
    }
  }
}`;
